import {
  DirectionBalanceSheetSettingsFormDto,
  type DirectionBalanceSheetSettingsFormInterface
} from '@zaoblako/shared/modules/catalog/dtos/direction/direction-balance-sheet-settings-form.dto';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { Erp1cAnalyticDimensionKeyEnum } from '@zaoblako/shared/modules/catalog/enums/erp-1c-analytic-dimension-key.enum';
import type { AccountDimensionSettingFiltersInterface } from '@zaoblako/shared/modules/catalog/dtos/account-dimension-setting/account-dimension-setting-filters.dto';
import type { ContractOwnFiltersInterface } from '@zaoblako/shared/modules/document/dtos/contract/contract-own-filters.dto';
import type { OtherFinancialFlowFiltersInterface } from '@zaoblako/shared/modules/catalog/dtos/other-financial-flow/other-financial-flow-filters.dto';
import type { WarehouseFiltersInterface } from '@zaoblako/shared/modules/catalog/dtos/warehouse/warehouse-filters.dto';
import type { CashFlowItemFiltersInterface } from '@zaoblako/shared/modules/catalog/dtos/cash-flow-item/cash-flow-item-filters.dto';
import type { CostItemFiltersInterface } from '@zaoblako/shared/modules/catalog/dtos/cost-item/cost-item-filters.dto';

import { StandardSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/standard-dropdown/standard-single-select/standard-single-select.component';
import { StandardMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/standard-dropdown/standard-multi-select/standard-multi-select.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { Erp1cAnalyticDimensionKeyNamePipe } from '@zaoblako/web/shared/pipes/erp-1c-analytic-dimension-key-name.pipe';
import { AccountDimensionSettingService } from '@zaoblako/web/core/services/catalog/account-dimension-setting.service';
import { OtherFinancialFlowService } from '@zaoblako/web/core/services/catalog/other-financial-flow.service';
import { WarehouseService } from '@zaoblako/web/core/services/catalog/warehouse.service';
import { CashFlowItemService } from '@zaoblako/web/core/services/catalog/cash-flow-item.service';
import { CostItemService } from '@zaoblako/web/core/services/catalog/cost-item.service';
import { ContragentService } from '@zaoblako/web/core/services/client/contragent.service';
import { ContractService } from '@zaoblako/web/core/services/finance/contract.service';

import { TooltipModule } from 'primeng/tooltip';
import { mapValues, compact } from 'lodash';
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  type OnInit
} from '@angular/core';

type ArrayElement<T> = T extends Array<infer Item> ? Item : never;

type ValueApiParams =
  OtherFinancialFlowFiltersInterface |
  WarehouseFiltersInterface |
  CashFlowItemFiltersInterface |
  CostItemFiltersInterface |
  ContractOwnFiltersInterface;

type SettingValues = Omit<DirectionBalanceSheetSettingsFormInterface, 'setting'>;

type SettingValueKeys = keyof SettingValues;

type SettingValueType<K extends SettingValueKeys> = ArrayElement<SettingValues[K]>

type SettingValueConnectionIdGetters = {
  [P in Exclude<SettingValueKeys, 'contragents'>]: (value: SettingValueType<P>) => number;
};

type SettingValueNameGetters = {
  [P in Exclude<SettingValueKeys, 'contragents'>]: (value: SettingValueType<P>) => string;
};

const DIMENSION_FIELD_MAP: Partial<Record<Erp1cAnalyticDimensionKeyEnum, SettingValueKeys>> = {
  [Erp1cAnalyticDimensionKeyEnum.OTHER_INCOME_EXPENSE]: 'otherFinancialFlows',
  [Erp1cAnalyticDimensionKeyEnum.WAREHOUSE]: 'warehouses',
  [Erp1cAnalyticDimensionKeyEnum.CASH_FLOW_ITEM]: 'cashFlowItems',
  [Erp1cAnalyticDimensionKeyEnum.COST_ITEM]: 'costItems',
  [Erp1cAnalyticDimensionKeyEnum.COUNTERPARTY]: 'contragents',
  [Erp1cAnalyticDimensionKeyEnum.CONTRACT]: 'contracts'
};

const SETTING_VALUE_CONNECTION_ID_GETTERS: SettingValueConnectionIdGetters = {
  contracts: (value): number => value.company?.connection?.id,
  warehouses: (value): number => value.connection?.id,
  costItems: (value): number => value.connection?.id,
  otherFinancialFlows: (value): number => value.connection?.id,
  cashFlowItems: (value): number => value.connection?.id
};

const SETTING_VALUE_NAME_GETTERS: SettingValueNameGetters = {
  contracts: (value): string => `№${value.number} "${value.name}"`,
  warehouses: (value): string => `"${value.name}"`,
  costItems: (value): string => `"${value.name}"`,
  otherFinancialFlows: (value): string => `"${value.name}"`,
  cashFlowItems: (value): string => `"${value.name}"`
};

@Component({
  standalone: true,
  selector: 'crm-direction-balance-sheet-settings-form',
  templateUrl: './direction-balance-sheet-settings-form.component.html',
  styleUrl: './direction-balance-sheet-settings-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    StandardSingleSelectComponent,
    StandardMultiSelectComponent,
    JoinPipe,
    Erp1cAnalyticDimensionKeyNamePipe,
    TooltipModule
  ]
})
export class DirectionBalanceSheetSettingsFormComponent implements OnInit {
  /** Settings form schema */
  public dto = DirectionBalanceSheetSettingsFormDto;

  /** Settings form */
  @Input() public settings = <DirectionBalanceSheetSettingsFormInterface>{};

  /** Account ID filter for account dimension settings */
  @Input()
  public set accountId(value: number) {
    if (value) {
      this.settingApiParams = { accountIds: [value] };
    } else {
      this.settingApiParams = {};
    }
    this.changeDetectorRef.markForCheck();
  }

  @Output() public onDeleteClick = new EventEmitter();

  /** Setting dropdown descriptor API route */
  public settingDropdownDescriptorApi: string;

  /** Dropdown descriptor APIs by dimensions */
  public dropdownDescriptorApis: Partial<Record<Erp1cAnalyticDimensionKeyEnum, string>>;

  /** Additional filters for settings dropdown */
  public settingApiParams?: AccountDimensionSettingFiltersInterface;

  public valueApiParams?: ValueApiParams;

  public errors?: Partial<Record<keyof DirectionBalanceSheetSettingsFormInterface, string[]>>;

  public hasErrors = false;

  public constructor(
    accountDimensionSettingService: AccountDimensionSettingService,
    otherFinancialFlowService: OtherFinancialFlowService,
    warehouseService: WarehouseService,
    cashFlowItemService: CashFlowItemService,
    costItemService: CostItemService,
    contragentService: ContragentService,
    contractService: ContractService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
    this.settingDropdownDescriptorApi = accountDimensionSettingService.dropdownDescriptorUrl;
    this.dropdownDescriptorApis = {
      [Erp1cAnalyticDimensionKeyEnum.OTHER_INCOME_EXPENSE]: otherFinancialFlowService.dropdownDescriptorApi,
      [Erp1cAnalyticDimensionKeyEnum.WAREHOUSE]: warehouseService.dropdownDescriptorApi,
      [Erp1cAnalyticDimensionKeyEnum.CASH_FLOW_ITEM]: cashFlowItemService.dropdownDescriptorApi,
      [Erp1cAnalyticDimensionKeyEnum.COST_ITEM]: costItemService.dropdownDescriptorApi,
      [Erp1cAnalyticDimensionKeyEnum.COUNTERPARTY]: contragentService.dropdownDescriptorApi,
      [Erp1cAnalyticDimensionKeyEnum.CONTRACT]: contractService.dropdownDescriptorApi
    };
  }

  public ngOnInit(): void {
    // Ensure fields are defined
    let fields = Object.values(DIMENSION_FIELD_MAP);
    for (const field of fields) {
      this.settings[field] = (this.settings[field] as any[]) || [];
    }
  }

  public validate(countBySettingIds: Record<number, number>): boolean {
    this.errors = {};
    this.hasErrors = false;
    this.changeDetectorRef.markForCheck();

    // Base validation
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, error] = DirectionBalanceSheetSettingsFormDto.validateSyncSafe(this.settings);
    if (error) {
      this.errors = mapValues(
        error.validationErrors,
        (err) => compact(
          Array.isArray(err)
            ? err.map((item) => item?.id)
            : [err?.id]
        )
      );
      this.hasErrors = true;
      this.changeDetectorRef.markForCheck();

      return false;
    }

    if (
      countBySettingIds &&
      this.settings?.setting?.id &&
      countBySettingIds[this.settings.setting.id] &&
      countBySettingIds[this.settings.setting.id] > 1
    ) {
      this.errors.setting = ['Субконто не должны дублироваться'];
      this.hasErrors = true;
      this.changeDetectorRef.markForCheck();
      return false;
    }

    // Validate if required list is not empty
    const dimension = this.settings.setting?.dimension;
    const dimensionField = DIMENSION_FIELD_MAP[dimension];
    const dimensionValues = this.settings[dimensionField];
    if (!dimensionValues?.length) {
      this.hasErrors = true;
      this.errors[dimensionField] = [DtoMessageEnum.EMPTY_ARRAY];
      this.changeDetectorRef.markForCheck();
      return false;
    }

    // If not counterparties then validate 1C ERP databases
    if (dimension !== Erp1cAnalyticDimensionKeyEnum.COUNTERPARTY) {
      const settingConnectionIds = (this.settings.setting?.connections || []).map((item) => item.id);
      const usedConnectionIds = new Set<number>();
      for (const dimensionValue of dimensionValues) {
        const connectionId = SETTING_VALUE_CONNECTION_ID_GETTERS[dimensionField](dimensionValue);
        const displayName = SETTING_VALUE_NAME_GETTERS[dimensionField](dimensionValue);

        // Value's 1C ERP database must be in dimension value databases list
        if (!settingConnectionIds.includes(connectionId)) {
          this.errors[dimensionField] = this.errors[dimensionField] || [];
          this.errors[dimensionField].push(`${displayName} - не входит в базы 1С данного субконто`);
          this.hasErrors = true;
        }

        // Only one value for one 1C ERP database can be selected
        if (usedConnectionIds.has(connectionId)) {
          this.errors[dimensionField] = this.errors[dimensionField] || [];
          this.errors[dimensionField].push(`${displayName} - нельзя выбирать два значения из одной базы 1С`);
          this.hasErrors = true;
        } else {
          usedConnectionIds.add(connectionId);
        }
      }

      if (this.hasErrors) {
        this.changeDetectorRef.markForCheck(); // Only errors can affect changes
        return false;
      }
    }

    return true;
  }

  /**
   * Additional handler for {@link DirectionBalanceSheetSettingsFormInterface.setting} field change
   */
  public onSettingChange() {
    if (!this.settings) {
      return;
    }

    // Reset other dimensions field
    const dimension = this.settings.setting?.dimension;
    let fields = Object.values(DIMENSION_FIELD_MAP);
    if (dimension) {
      fields = fields.filter((field) => field !== DIMENSION_FIELD_MAP[dimension])
    }
    for (const field of fields) {
      this.settings[field] = [];
    }

    // Refresh API parameters for select
    if (dimension && dimension !== Erp1cAnalyticDimensionKeyEnum.COUNTERPARTY) {
      const connectionIds = (this.settings.setting?.connections || []).map((item) => item.id);
      this.valueApiParams = { connectionIds };
      if (dimension === Erp1cAnalyticDimensionKeyEnum.CONTRACT) {
        (this.valueApiParams as ContractOwnFiltersInterface).withConnections = true;
      }
    } else {
      this.valueApiParams = undefined;
    }

    this.changeDetectorRef.markForCheck();
  }

  /**
   * Get selected values by dimension
   *
   * @param dimension Analytic dimension
   *
   * @returns Selected values list
   */
  public getSelectedValues(dimension: Erp1cAnalyticDimensionKeyEnum): any[] {
    const field = DIMENSION_FIELD_MAP[dimension];
    return field
      ? this.settings?.[field]
      : undefined;
  }

  /**
   * Set selected values for dimension
   *
   * @param dimension Analytic dimension
   * @param values Values list to set
   */
  public setSelectedValues(dimension: Erp1cAnalyticDimensionKeyEnum, values: any[]): void {
    const field = DIMENSION_FIELD_MAP[dimension];
    if (field && this.settings) {
      this.settings[field] = values;
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Get validation error(s) for dimension
   *
   * @param dimension Analytic dimension
   *
   * @returns Validation error(s)
   */
  public getValuesErrors(dimension: Erp1cAnalyticDimensionKeyEnum) {
    const field = DIMENSION_FIELD_MAP[dimension];
    return field
      ? this.errors?.[field]
      : undefined;
  }
}
